"use client";

import { useState, useEffect, useCallback } from "react";
import {
  StoredConsent,
  CONSENT_CHANGE_EVENT,
  CONSENT_STORAGE_KEY,
  getConsent,
  openCookiePreferences,
} from "@/lib/cookieConsent";

export function useCookieConsent(): {
  consent: StoredConsent | null;
  thirdPartyAllowed: boolean;
  analyticsAllowed: boolean;
  openPreferences: () => void;
} {
  const [consent, setConsentState] = useState<StoredConsent | null>(() => {
    return getConsent();
  });

  const syncConsent = useCallback(() => {
    setConsentState(getConsent());
  }, []);

  useEffect(() => {
    // Initial sync
    syncConsent();

    const handleConsentChange = (e: Event) => {
      const custom = e as CustomEvent<StoredConsent>;
      if (custom.detail) {
        setConsentState(custom.detail);
      } else {
        syncConsent();
      }
    };

    const handleStorage = (e: StorageEvent) => {
      if (!e.key || e.key === CONSENT_STORAGE_KEY) {
        syncConsent();
      }
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, handleConsentChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, handleConsentChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [syncConsent]);

  const openPreferences = useCallback(() => {
    openCookiePreferences();
  }, []);

  return {
    consent,
    thirdPartyAllowed: Boolean(consent && consent.thirdParty),
    analyticsAllowed: Boolean(consent && consent.analytics),
    openPreferences,
  };
}
